import { useState, useRef, useEffect } from 'react'
import ReactPlayer from 'react-player'

import LoadingIndicator from '../LoadingIndicator/LoadingIndicator'
import useWindowSize from '../../hooks/useWindowSize'
import { getCssVar } from '../../utils/css'

export default function Embed (props = {
    oembed: null,
    className: null,
    color: null,
    soundcloudVisualPlayer: true
}) {
    if (!props.oembed)
        return null

    const [player, setPlayer] = useState({
        error: false,
        ready: false,
        width: null,
        height: null
    })

    const embed = useRef()

    const getEmbedIframe = () => embed.current
        && embed.current.querySelector('iframe')

    let url = props.oembed

    const anchorFmExp
        = /^(https*:\/\/anchor\.fm\/[^/]+\/)(episodes\/.+)(?!\.[a-zA-Z0-9])$/

    const isAnchorFm = Boolean(url.match(anchorFmExp))

    url = isAnchorFm
        ? url.replace(anchorFmExp, '$1embed/$2')
        : url

    const anchorFmHeight = 102

    const anchorFmHeightLg = 161

    const size = useWindowSize()

    useEffect(() => {
        const iframe = getEmbedIframe()

        if (!iframe
                || !isAnchorFm)
            return

        const stateObj = {}

        if (!player.ready)
            stateObj.ready = true

        if (iframe.clientWidth < 768
                && player.height !== anchorFmHeight)
            stateObj.height = anchorFmHeight

        if (iframe.clientWidth >= 768
                && player.height !== anchorFmHeightLg)
            stateObj.height = anchorFmHeightLg

        if (!Object.keys(stateObj).length)
            return

        stateObj.ready = true

        setPlayer(stateObj)
    }, [size, embed])

    const onReactPlayerReady = (player) => {
        console.log(player)
        const iframe = getEmbedIframe()

        if (iframe
                && iframe.getAttribute('frameborder'))
            iframe.removeAttribute('frameborder')

        setPlayer({
            ready: true,
            width: iframe
                    && iframe.getAttribute('width')
                    && iframe.getAttribute('width') !== 'null'
                    && iframe.getAttribute('width') !== '100%'
                ? parseFloat(iframe.getAttribute('width'))
                : null,
            height: iframe
                    && iframe.getAttribute('height')
                    && iframe.getAttribute('height') !== 'null'
                    && iframe.getAttribute('height') !== '100%'
                ? parseFloat(iframe.getAttribute('height'))
                : null
        })

        Array.from(document.querySelectorAll('script[charset]'))
            .forEach(el => el.removeAttribute('charset'))
    }

    const isRatioWide = props.oembed.match(/(?:vimeo|youtube)\.com/)
        || props.oembed.match(/youtu\.be/)
        || props.oembed.match(
            /https?:\/\/[^.]+\.(wistia\.com|wi\.st)\/(medias|embed)\/.*/)
        || (props.oembed.match(/soundcloud\.com/)
            && props.soundcloudVisualPlayer)

    const className = `embed${
        isRatioWide
            ? ' ratio-wide'
            : ''} ${props.className || ''}${
        player.error
            ? ' embed-error'
            : ''}${
        isAnchorFm
            ? ' embed-anchorfm'
            : ''}`
        .trim()

    const color = (props.color
        || getCssVar(`--${(url || '').match(/wistia\.com/)
            ? 'navy'
            : 'aqua'}`)
        || '')
        .replace(/^#/, '')

    return (
        <div
            ref={embed}
            className={className}
            style={{
                paddingBottom: !player.error
                    && player.width
                    && player.height
                    && `${((player.height / player.width) * 100)}%`
            }}
        >
            {!player.error
                && !player.ready
                && !(url || '').match(/(wistia\.com|wi\.st|\.mp3|\.m4a)/)
                && <LoadingIndicator />}
            {isAnchorFm
                && (
                    <iframe
                        className="embed-player"
                        src={url}
                        width="400"
                        height={player.height || anchorFmHeight}
                        scrolling="no"
                        style={{
                            opacity: player.ready
                                ? 1
                                : 0
                        }}
                    />)}
            {!player.error
                && !isAnchorFm
                && (
                    <ReactPlayer
                        className="embed-player"
                        url={url}
                        width="100%"
                        height={isRatioWide
                            ? '100%'
                            : null}
                        playsinline
                        controls
                        config={{
                            vimeo: { playerOptions: { color: color } },
                            wistia: { options: { playerColor: color } },
                            soundcloud: { options: { visual: props.soundcloudVisualPlayer } }
                        }}
                        style={{
                            position: player.width
                                && player.height
                                && 'absolute',
                            opacity: player.ready
                                    || (url || '')
                                        .match(/(wistia\.com|wi\.st)/)
                                ? 1
                                : 0,
                            transition: (url || '').match(/vimeo\.com/)
                                ? '600ms opacity 200ms'
                                : null
                        }}
                        onReady={onReactPlayerReady}
                        onError={() => setPlayer({
                            error: true
                        })}
                    />)}
            {player.error
                && (
                    <div
                        className="embed-error-message"
                        role="status"
                    >
                        <svg
                            className="icon-error flex-none"
                            width="36"
                            height="36"
                        >
                            <use xlinkHref="#icon-error" />
                        </svg>
                        <h2
                            className="heading-5 font-weight-normal"
                        >
                            Error loading media.
                        </h2>
                        <p>Please try again later</p>
                    </div>)}
        </div>
    )
}
